import React, { useMemo, useState } from "react";
import "./ExamPage_reading.css";
import "./ReadingReviewIndex.css";

/**
 * ReadingReviewOC
 * 
 * Handles OC-specific data variations safely
 */
export default function ReadingReviewOC({
  questions = [],
  attemptDates = [],
  selectedSessionId,
  setSelectedSessionId,
  onSessionChange,
  mode,
  onExit
}) {
  const [index, setIndex] = useState(0);
  const [showQuestionNavigator, setShowQuestionNavigator] =
  useState(false);
  const [explanations, setExplanations] = useState({});
  const [loadingExplanation, setLoadingExplanation] = useState(null);
  const [activeExtract, setActiveExtract] = useState(0);
  console.log("📅 REVIEW attemptDates:", attemptDates);

  const API_BASE = process.env.REACT_APP_API_URL;

  /* =============================
     GROUP QUESTIONS BY TOPIC
  ============================= */
  const groupedQuestions = useMemo(() => {
    const g = {};

    questions.forEach((q, i) => {
      const key = q.topic || q.question_type || "Other";

      if (!g[key]) {
        g[key] = {
          topic: key,
          indexes: []
        };
      }

      g[key].indexes.push(i);
    });

    return g;
  }, [questions]);

  const currentQuestion = questions[index];
  console.log(
    "📚 REVIEW QUESTIONS",
    questions.length,
    questions[0]?.question_id,
    selectedSessionId
  );

  if (!currentQuestion) {
    return <div>No review data available.</div>;
  }

  const qid = String(currentQuestion.question_id);

  /* =============================
     SAFE OPTIONS (OC FIX)
  ============================= */
  const options =
    currentQuestion.answer_options ||
    currentQuestion.options ||
    currentQuestion.section_ref?.answer_options ||
    {};

  /* =============================
     SAFE PASSAGE (OC FIX)
  ============================= */
  let rm =
    currentQuestion.reading_material ||
    currentQuestion.passage ||
    currentQuestion.stimulus ||
    currentQuestion.section_ref?.reading_material ||
    {};

  if (typeof rm === "string") {
    rm = { content: rm };
  }

  const passageStyle =
    currentQuestion.passage_style ||
    currentQuestion.section_ref?.passage_style ||
    "informational";

  const currentQuestionType =
    currentQuestion.question_type ||
    currentQuestion.section_ref?.question_type ||
    currentQuestion.topic?.toLowerCase().replace(/\s+/g, "_") ||
    "";

  const isDropdownCloze =
    currentQuestionType === "dropdown_cloze";

  /* =============================
     HELPERS
  ============================= */
  const getQuestionStatus = (q) => {
    if (!q.student_answer) return "skipped";
    if (q.is_correct) return "correct";
    return "wrong";
  };

  const prettyTopic = (t = "Other") =>
    t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const formatExplanation = (text) => {
    if (!text) return "";

    return text
      .replace(/\*\*(.*?)\*\*/g, "<strong style='display:block; margin-top:10px;'>$1</strong>")
      .replace(/\n\n/g, "<br/><br/>")
      .replace(/\n/g, "<br/>");
  };

  /* =============================
     AI EXPLANATION (OC ENDPOINT)
  ============================= */
  const handleGenerateExplanation = async (question) => {
    const qid = String(question.question_id);

    if (explanations[qid]) return;

    setLoadingExplanation(qid);

    try {
      const res = await fetch(
        `${API_BASE}/api/ai/explain-question-selective-reading`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            question_text: question.question_text,
            options:
              question.answer_options ||
              question.options ||
              {},
            correct_answer: question.correct_answer,
            passage:
              question.reading_material ||
              question.passage ||
              question.stimulus
          })
        }
      );

      if (!res.ok) throw new Error("API failed");

      const data = await res.json();

      setExplanations((prev) => ({
        ...prev,
        [qid]: data.explanation || "⚠️ Failed to generate explanation."
      }));
    } catch (err) {
      console.error("Explanation failed", err);

      setExplanations((prev) => ({
        ...prev,
        [qid]: "⚠️ Failed to generate explanation."
      }));
    } finally {
      setLoadingExplanation(null);
    }
  };

  /* =============================
     UI
  ============================= */
  return (
    <div className="exam-container review-mode">
      {/* HEADER */}
      <div className="exam-header">
  <div>OC Reading Review</div>

  <div
    className="question-counter-inline"
    style={{
      display: "flex",
      alignItems: "center",
      gap: "10px"
    }}
  >
    <span className="question-counter-text">
      Question {index + 1} of {questions.length}
    </span>

    <button
      className="question-grid-toggle"
      onClick={() => setShowQuestionNavigator(prev => !prev)}
    >
      ▦
    </button>

    {onExit && (
      <button
        className="exit-review-btn"
        onClick={onExit}
      >
        ← Back to Report
      </button>
    )}

    <select
      className="review-exam-dropdown"
      value={selectedSessionId || ""}
      onChange={(e) => {
        const sessionId = e.target.value;
        setSelectedSessionId(sessionId);
        onSessionChange(sessionId);
      }}
    >
      {attemptDates.map((a) => (
        <option
          key={a.session_id}
          value={a.session_id}
        >
          {new Date(a.created_at).toLocaleString()}
        </option>
      ))}
    </select>
  </div>

  {/* REVIEW NAV BUTTONS */}
  <div className="top-nav-buttons">
    <button
      className="nav-btn prev"
      disabled={index === 0}
      onClick={() => setIndex(index - 1)}
    >
      Previous
    </button>

    <button
      className="nav-btn next"
      disabled={index === questions.length - 1}
      onClick={() => setIndex(index + 1)}
    >
      Next
    </button>
  </div>
</div>
      
      {/* QUESTION INDEX */}
      {showQuestionNavigator && (
      <div className="question-index-grouped">
        {Object.entries(groupedQuestions).map(([key, data]) => (
          <div key={key} className="topic-group">
            <div className="topic-title">
              {prettyTopic(data.topic)}
            </div>

            <div className="topic-circles">
              {data.indexes.map((i) => {
                const q = questions[i];

                console.log(
                  "🟢 REVIEW STATUS",
                  q.question_id,
                  q.student_answer,
                  q.is_correct,
                  getQuestionStatus(q)
                );
                const cls = [
                  "index-circle",
                  "review",
                  getQuestionStatus(q),
                  i === index ? "active" : ""
                ].join(" ");

                return (
                  <div
                    key={q.question_id}
                    className={cls}
                    onClick={() => setIndex(i)}
                  >
                    {i + 1}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      )}

      {/* BODY */}
      <div className="exam-body">
        {/* PASSAGE */}
        <div className={`passage-pane ${passageStyle}`}>
          {rm?.title && <h3>{rm.title}</h3>}
        
          {/* ===================================== */}
          {/* EXTRACT MATCHING (NEW ARRAY FORMAT) */}
          {/* ===================================== */}

          {Array.isArray(rm?.extracts) &&
          rm.extracts.length > 0 ? (

            <div className="extract-matching-container">

              {/* ========================= */}
              {/* EXTRACT TABS */}
              {/* ========================= */}

              <div className="extract-tabs">

                {rm.extracts.map((extract, idx) => (

                  <button
                    key={extract.label}
                    className={`extract-tab ${
                      activeExtract === idx
                        ? "active"
                        : ""
                    }`}
                    onClick={() =>
                      setActiveExtract(idx)
                    }
                  >
                    Extract {extract.label}
                  </button>

                ))}

              </div>

              {/* ========================= */}
              {/* ACTIVE EXTRACT */}
              {/* ========================= */}

              <div className="extract-panel">

                <h3>
                  Extract {
                    rm.extracts[activeExtract]?.label
                  }
                </h3>

                {rm.extracts[activeExtract]?.title && (
                  <h4>
                    {
                      rm.extracts[activeExtract].title
                    }
                  </h4>
                )}

                <p>
                  {
                    rm.extracts[activeExtract]?.content
                  }
                </p>

              </div>

            </div>

          ) :

          /* ===================================== */
          /* LEGACY OBJECT FORMAT SUPPORT */
          /* ===================================== */

          rm?.extracts &&
          typeof rm.extracts === "object" &&
          !Array.isArray(rm.extracts) ? (

            <div className="extracts">

              {Object.entries(rm.extracts).map(
                ([key, text]) => (

                  <div
                    key={key}
                    className="extract"
                  >

                    <strong>
                      Extract {key}
                    </strong>

                    <p>{text}</p>

                  </div>
                )
              )}

            </div>

          ) : null}
        
          {rm?.content && (

            isDropdownCloze ? (

              <div className="reading-content dropdown-cloze-passage">

                {(() => {

                  const questionMap = {};

                  questions.forEach((q) => {
                    questionMap[q.placeholder] = q;
                  });

                  const normalizedContent = rm.content
                    .replace(/\n+/g, " ")
                    .replace(/\s+/g, " ")
                    .trim();

                  const parts = normalizedContent.split(
                    /(\[GAP_\d+\])/
                  );

                  return parts.map((part, idx) => {

                    const gapMatch = part.match(
                      /\[(GAP_\d+)\]/
                    );

                    // NORMAL TEXT
                    if (!gapMatch) {
                      return (
                        <span key={idx}>
                          {part}
                        </span>
                      );
                    }

                    // GAP
                    const placeholder = gapMatch[1];

                    const gapQuestion =
                      questionMap[placeholder];

                    if (!gapQuestion) {
                      return (
                        <span key={idx}>
                          {part}
                        </span>
                      );
                    }

                    const selected =
                      gapQuestion.student_answer;

                    const correct =
                      gapQuestion.correct_answer;

                    let cls =
                      "dropdown-cloze-review";

                    if (!selected) {
                      cls += " skipped";
                    }
                    else if (selected === correct) {
                      cls += " correct";
                    }
                    else {
                      cls += " wrong";
                    }

                    return (
                      <select
                        key={idx}
                        disabled
                        className={cls}
                        value={selected || ""}
                      >
                        <option value="">
                          Select
                        </option>

                        {Object.entries(
                          gapQuestion.answer_options || {}
                        ).map(([k, v]) => (
                          <option key={k} value={k}>
                            {v}
                          </option>
                        ))}
                      </select>
                    );
                  });

                })()}

              </div>

            ) : (

              <p className="reading-content">
                {rm.content}
              </p>

            )

          )}
        
          {rm?.paragraphs &&
            Object.entries(rm.paragraphs).map(([k, v]) => (
              <p key={k} className="reading-paragraph">
                <strong>{k}.</strong> {v}
              </p>
            ))}
        </div>

        {/* QUESTION */}
        <div className="question-pane">
          <div className="question-header-row">
            
            <p className="question-text">
              {String(currentQuestion.question_text).replace(
                /^Q?\d+\.\s*/i,
                ""
              )}
            </p>
            

            {!explanations[qid] && (
              <button
                className="ai-explain-btn-inline"
                onClick={() => handleGenerateExplanation(currentQuestion)}
                disabled={loadingExplanation === qid}
              >
                {loadingExplanation === qid ? "..." : "💡 Explain"}
              </button>
            )}
          </div>

          {/* OPTIONS */}
          
          <div className="options">     
            {Object.keys(options).length === 0 && (
              <div className="no-options-warning">
                ⚠️ No answer options available
              </div>
            )}

            {Object.entries(options).map(([k, v]) => {
              console.log(currentQuestion);

              let cls = "option-btn";

                const studentAnswer =
                  currentQuestion.student_answer;

                const correctAnswer =
                  currentQuestion.correct_answer;

                // ✅ Student selected correct answer
                if (
                  k === correctAnswer &&
                  studentAnswer === correctAnswer
                ) {
                  cls += " option-correct";
                }

                // ❌ Correct answer (when student got it wrong)
                else if (
                  k === correctAnswer &&
                  studentAnswer !== correctAnswer
                ) {
                  cls += " option-correct";
                }

                // ❌ Wrong selected answer
                else if (
                  k === studentAnswer &&
                  studentAnswer !== correctAnswer
                ) {
                  cls += " option-wrong";
                }

              return (
                <button key={k} disabled className={cls}>
                  <strong>{k}.</strong> {v}
                </button>
              );

            })}
          </div>
        
          {/* EXPLANATION */}
          {explanations[qid] && (
            <div className="ai-explanation-box">
              <h4>Explanation</h4>
              <div
                dangerouslySetInnerHTML={{
                  __html: formatExplanation(explanations[qid])
                }}
              />
            </div>
          )}

          
        </div>
      </div>
    </div>
  );
}
